/* ==========================================================================
   pdp-fit-bar.js
   PDP vehicle-fit bar. The markup and its position now come from Liquid
   (snippets/pdp-fitment.liquid, rendered by snippets/product-info.liquid),
   so all this file does is hydrate the year/make/model picker and keep the
   "see parts that fit" link pointed at the shopper's vehicle.

   It also runs the reveal gate: the bar stays hidden (space reserved) until
   fitment has resolved, so shoppers never see the no-vehicle state flash
   before the correct fits/nofit bar. See the reveal gate note in
   pdp-fit-bar.css.

   (The A/B test scaffolding - variant class check, DOM relocation and QA
   query param - is gone.)

   Must load after theme-custom.js, which defines <ymm-selector>.
   ========================================================================== */

(function () {
  'use strict';

  /* -- Targets ----------------------------------------------------------- *
   * Resolved per product column. main-product.liquid renders product-form
   * directly for the quick-buy drawer and popover; those have no
   * .product__info wrapper and no fit block at all, so nothing matches
   * there. The CSS is scoped the same way.
   * --------------------------------------------------------------------- */
  function eachTarget(fn) {
    var wrappers = document.querySelectorAll('[data-bafit-wrapper]');
    for (var i = 0; i < wrappers.length; i++) {
      var wrapper = wrappers[i];
      var column = wrapper.closest ? wrapper.closest('.product__info') : null;
      if (!column) continue;
      fn(wrapper);
    }
  }

  /* -- Reveal gate ------------------------------------------------------- *
   * theme-custom.js drops <ymm-fitment> to opacity 0 while it resolves
   * fitment and sets it back to 1 once getFitmentTableData() has answered.
   * That return to 1 is the signal the bar is showing its final state, so
   * release the gate on it.
   *
   * The failsafe here is needed on top of the one in theme-custom.js: that
   * one only arms after the header selector's dropdown data has loaded, so
   * if the dropdowns API is down it never starts. Counting from page load
   * covers that case.
   * --------------------------------------------------------------------- */
  var REVEAL_FAILSAFE_MS = 2500;

  function armReveal(wrapper) {
    if (wrapper.getAttribute('data-bafit-armed') === 'true') return;
    wrapper.setAttribute('data-bafit-armed', 'true');

    var fit = wrapper.querySelector('ymm-fitment');
    if (!fit) {
      markReady(wrapper);
      return;
    }

    // Only the inline value is trustworthy. Computed opacity is already 1 at
    // first paint, so checking getComputedStyle would release the gate
    // immediately and defeat the point.
    if (fit.style.opacity === '1') {
      markReady(wrapper);
      return;
    }

    var timer;
    var observer = new MutationObserver(function () {
      if (fit.style.opacity !== '1') return;
      markReady(wrapper);
      observer.disconnect();
      clearTimeout(timer);
    });
    observer.observe(fit, { attributes: true, attributeFilter: ['style'] });

    timer = setTimeout(function () {
      // data-state is "" at this point, which is the right fallback: ask for
      // the vehicle rather than leave a blank space.
      fit.style.opacity = '1';
      markReady(wrapper);
      observer.disconnect();
    }, REVEAL_FAILSAFE_MS);
  }

  function markReady(wrapper) {
    wrapper.setAttribute('data-bafit-ready', 'true');
  }

  /* -- Picker hydration -------------------------------------------------- *
   * The <ymm-selector> for the no-vehicle state ships inside a <template>
   * and is cloned in only when it is actually needed. That keeps exactly one
   * live <ymm-selector> on the page until the shopper has no vehicle set, so
   * the PDP picker does not join the global querySelectorAll('ymm-selector')
   * loops in save() / resetAllNextSelectors() before it exists.
   *
   * Hydration waits for ymmData:Loaded, which the header selector dispatches
   * once loadDropdownData() has resolved. By then ymmDropdownData is cached
   * module-side, so initialize() populates the selects with zero network
   * calls.
   *
   * Do NOT replace this with an `auto-fetch` attribute on the selector.
   * loadDropdownData() has no in-flight dedupe, so the header selector and
   * this picker would both fire /api/dropdowns in parallel on every PDP load.
   * --------------------------------------------------------------------- */
  var ymmDataReady = false;

  function hydratePicker() {
    if (!ymmDataReady) return;

    eachTarget(function (wrapper) {
      var mount = wrapper.querySelector('.bafit__picker-mount');
      var template = wrapper.querySelector('.bafit__picker-template');
      if (!mount || !template) return;
      if (mount.getAttribute('data-bafit-hydrated') === 'true') return;

      // Nothing to pick if a vehicle is already set. If the shopper later
      // resets, data-state returns to "" and ymmData:Changed brings us back.
      var fit = wrapper.querySelector('ymm-fitment');
      if (fit && fit.getAttribute('data-state') !== '') return;

      mount.appendChild(template.content.cloneNode(true));
      mount.setAttribute('data-bafit-hydrated', 'true');

      var picker = mount.querySelector('ymm-selector');
      if (!picker || typeof picker.initialize !== 'function') {
        rollbackPicker(mount);
        return;
      }

      var pending;
      try {
        pending = picker.initialize();
      } catch (e) {
        rollbackPicker(mount);
        return;
      }
      if (pending && typeof pending.catch === 'function') {
        pending.catch(function () {
          // Dropdowns API unreachable - drop back to the jsYMMBtn fallback,
          // which opens the header selector.
          rollbackPicker(mount);
        });
      }
    });
  }

  function rollbackPicker(mount) {
    mount.removeAttribute('data-bafit-hydrated');
    while (mount.firstChild) mount.removeChild(mount.firstChild);
  }

  /* -- Alternatives link ------------------------------------------------- *
   * Mirrors what jsYMMSubmit does: the YMM collection URL plus the vehicle as
   * query params, which getYMMParamsFromURL() reads back on landing.
   *
   * Deliberately labelled "parts that fit" rather than the product type. The
   * destination is every fitting product, not tow mirrors specifically -
   * there is no product-type facet on that route - and the label should not
   * promise narrower results than the page delivers.
   * --------------------------------------------------------------------- */
  function vehicleQuery() {
    var keys = ['year', 'make', 'model'];
    var out = [];
    for (var i = 0; i < keys.length; i++) {
      var value = null;
      try {
        value = localStorage.getItem('ymm-' + keys[i]);
      } catch (e) {
        /* ignore */
      }
      if (value && value.trim()) {
        out.push(encodeURIComponent(keys[i]) + '=' + encodeURIComponent(value));
      }
    }
    return out.join('&');
  }

  function syncAltLinks() {
    var query = vehicleQuery();
    eachTarget(function (wrapper) {
      var base = wrapper.getAttribute('data-bafit-alt-url') || '/collections/all';
      var href = base;
      if (query) href += (base.indexOf('?') === -1 ? '?' : '&') + query;
      var links = wrapper.querySelectorAll('[data-bafit-alt-link]');
      for (var i = 0; i < links.length; i++) {
        links[i].setAttribute('href', href);
      }
    });
  }

  /* -- Wiring ------------------------------------------------------------ */
  document.addEventListener('ymmData:Loaded', function () {
    ymmDataReady = true;
    hydratePicker();
    syncAltLinks();
  });

  document.addEventListener('ymmData:Changed', function () {
    hydratePicker();
    syncAltLinks();
  });

  function init() {
    eachTarget(armReveal);
    syncAltLinks();
    hydratePicker();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
